// Modified VenueMap component
"use client";

import React, { useEffect, useRef } from "react";

import { ExternalLink } from "lucide-react";
import { VenueWithLocations } from "@punchup/venues";

interface VenueLocation {
  address: string;
  city: string;
  state: string;
  postal_code: string;
  location_geo?:
    | {
        coordinates: number[];
      }
    | Record<string, never>;
}

export interface Venue {
  id: string;
  name: string;
  is_location_hidden: boolean;
  locations: VenueLocation[];
  city_state: string;
}

interface VenueMapProps {
  venue: VenueWithLocations;
  containerStyle?: React.CSSProperties;
  height?: React.CSSProperties["height"];
  width?: React.CSSProperties["width"];
  hideTitle?: boolean;
}

const defaultContainerStyle = {
  borderRadius: "4px",
};

const VenueMap: React.FC<VenueMapProps> = ({
  venue,
  containerStyle = defaultContainerStyle,
  height = "230px",
  width = "100%",
  hideTitle = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);

  const googleMapsKey = process.env.GOOGLE_MAPS_KEY;
  if (!googleMapsKey) {
    console.error(
      "Google Maps API key is missing. Please define GOOGLE_MAPS_KEY in your environment."
    );
  }

  const loadGoogleMapsScript = (callback: () => void) => {
    if (window.google && window.google.maps) {
      callback();
    } else {
      const existingScript = document.getElementById("googleMaps");
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}`;
        script.id = "googleMaps";
        script.async = true;
        script.defer = true;
        script.onload = callback;
        document.body.appendChild(script);
      } else {
        existingScript.addEventListener("load", callback);
      }
    }
  };

  // Type guard to check if location_geo has valid coordinates
  const hasValidCoordinates = (
    geo: unknown
  ): geo is { coordinates: number[] } => {
    if (typeof geo !== "object" || geo === null) return false;
    if (!("coordinates" in geo)) return false;
    const maybeCoordinates = (geo as { coordinates?: unknown }).coordinates;
    return Array.isArray(maybeCoordinates);
  };

  // Compute markers from the venue's locations (if not hidden)
  const computedMarkers =
    !venue.is_location_hidden && venue.locations && venue.locations.length > 0
      ? venue.locations
          .filter(
            (loc) =>
              hasValidCoordinates(loc.location_geo) &&
              loc.location_geo.coordinates.length >= 2
          )
          .map((loc) => ({
            // Swap the order: Google Maps expects [lat, lng]
            coordinate: [
              (loc.location_geo as { coordinates: number[] }).coordinates[1],
              (loc.location_geo as { coordinates: number[] }).coordinates[0],
            ] as [number, number],
            address: `${loc.address}, ${loc.city}, ${loc.state} ${loc.postal_code}`,
          }))
      : [];

  const isSingle = computedMarkers.length === 1;
  const isMultiple = computedMarkers.length > 1;

  // For single location mode, compute address details and Google Maps URL.
  let singleVenueAddress: string | undefined = undefined;
  let streetAddress = "";
  let cityStateZip = "";
  let googleMapsUrl = "";
  if (isSingle) {
    const marker = computedMarkers[0];
    singleVenueAddress = marker.address;
    if (marker.address) {
      const parts = marker.address.split(",");
      if (parts.length > 1) {
        streetAddress = parts[0].trim();
        cityStateZip = parts.slice(1).join(",").trim();
      } else {
        streetAddress = marker.address;
      }
      googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        venue.name
      )},${encodeURIComponent(marker.address)}`;
    }
  }

  const initMap = () => {
    if (!mapRef.current || !window.google) return;

    if (isMultiple) {
      // Multiple locations mode.
      const firstMarker = computedMarkers[0];
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: {
          lat: firstMarker.coordinate[0],
          lng: firstMarker.coordinate[1],
        },
        zoom: 13,
        disableDefaultUI: true,
      });

      const bounds = new window.google.maps.LatLngBounds();

      computedMarkers.forEach((markerData) => {
        const position = {
          lat: markerData.coordinate[0],
          lng: markerData.coordinate[1],
        };

        const marker = new window.google.maps.Marker({
          position,
          map: mapInstance.current!,
        });

        const parts = markerData.address.split(",");
        const streetAddress =
          parts.length > 1 ? parts[0].trim() : markerData.address;
        const cityStateZip =
          parts.length > 1 ? parts.slice(1).join(",").trim() : "";
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          venue.name
        )},${encodeURIComponent(markerData.address)}`;

        const contentString = `<div style="padding: 0; margin: 0;">
            <p style="font-size: 16px; font-weight: bold; margin: 0;">${venue.name}</p>
            <div style="margin-top: 4px;">
              <div>${streetAddress}</div>
              <div>${cityStateZip}</div>
            </div>
            <div style="margin-top: 8px;">
              <a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer" style="color: blue; text-decoration: underline;">
                View on Google Maps
              </a>
            </div>
          </div>`;

        const infoWindow = new window.google.maps.InfoWindow({
          content: contentString,
        });

        marker.addListener("click", () => {
          infoWindow.open({
            anchor: marker,
            map: mapInstance.current,
            shouldFocus: false,
          });
        });

        bounds.extend(position);
      });

      // If only one marker is present, set a default zoom level.
      if (computedMarkers.length === 1) {
        mapInstance.current.setZoom(13);
      } else {
        mapInstance.current.fitBounds(bounds);
      }
    } else if (isSingle) {
      // Single location mode.
      const markerData = computedMarkers[0];
      const center = {
        lat: markerData.coordinate[0],
        lng: markerData.coordinate[1],
      };

      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 13,
        disableDefaultUI: true,
      });

      const marker = new window.google.maps.Marker({
        position: center,
        map: mapInstance.current,
      });

      let contentString = `<div style="padding: 0; margin: 0;">
        <p style="font-size: 16px; font-weight: bold; margin: 0;">${venue.name}</p>`;
      if (singleVenueAddress) {
        contentString += `<div style="margin-top: 4px;">
          <div>${streetAddress}</div>
          <div>${cityStateZip}</div>
        </div>`;
      }
      contentString += `<div style="margin-top: 8px;">
          <a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer" style="color: blue; text-decoration: underline;">
            View on Google Maps
          </a>
        </div>
      </div>`;

      const infoWindow = new window.google.maps.InfoWindow({
        content: contentString,
      });

      marker.addListener("click", () => {
        infoWindow.open({
          anchor: marker,
          map: mapInstance.current,
          shouldFocus: false,
        });
      });
    }
  };

  useEffect(() => {
    if (computedMarkers.length === 0 || !mapRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadGoogleMapsScript(initMap);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(mapRef.current);

    return () => observer.disconnect();
  }, [venue]);

  return (
    <>
      {hideTitle ? null : isMultiple ? (
        // For multiple locations, display the venue name and a "Multiple Locations" label in a single flex column.
        <div className="flex flex-col gap-1">
          <p className="text-base text-primary-blue font-bold">{venue.name}</p>
          <p className="text-base text-secondary-blue font-bold">
            Multiple Locations
          </p>
        </div>
      ) : isSingle ? (
        // For a single location, show the venue name, address, and an ExternalLink icon.
        <>
          <div className="flex items-center justify-between">
            <p className="text-base text-primary-blue font-bold">
              {venue.name}
            </p>
          </div>
          {singleVenueAddress && (
            <div className="flex justify-between mt-1 text-lg text-secondary-blue">
              <p className="text-base text-secondary-blue">{streetAddress}</p>
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={24} className="text-primary" />
              </a>
            </div>
          )}
        </>
      ) : null}
      {/* Google Map container */}
      <div
        className="mt-4"
        style={{
          height,
          width,
          ...containerStyle,
        }}
        ref={mapRef}
      />
    </>
  );
};

export default VenueMap;
